import { PickerColumnItem } from 'antd-mobile/es/components/picker-view';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectPicker } from '../../../../components/common/inputs/SelectPicker';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import { SurveyNakedInput } from '../../../../components/common/survey/inputs/SurveyNakedInput';
import { SurveyOverviewNavigationList } from '../../../../components/common/survey/inputs/SurveyOverviewNavigationList';
import { SearchModelModal } from '../../../../components/custom/modal/toast/SearchModelModal';
import { useGetBrandsQuery } from '../../../../hooks/api/brand/queries/useGetBrands.query';
import { useGetMachinesQuery } from '../../../../hooks/api/machines/queries/useGetMachines.query';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { ownTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { userOwnTractorSurveySlice } from '../../../../store/userOwnTractorSurveySlice';
import { OwnTractorSurveyPageWrapper } from '../OwnTractorSurveyPageWrapper';
import { ownTractorSurveySteps } from '../../../../constants/survey/ownTractorSurvey/ownTractorSurveyBrand';
import { Helmet } from 'react-helmet';
import { IMachineListMachine } from '../../../../interfaces/Dictionary.interfaces';

const ERROR_MESSAGES = {
  brand: {
    title: '브랜드를 선택해 주세요',
    description: '',
  },
  model: {
    title: '모델을 선택해 주세요',
    description: '',
  },
  engineOutputInPs: {
    title: '마력을 입력해 주세요',
    description: '',
  },
  manufacturedYear: {
    title: '매물 연식을 선택해 주세요',
    description: '',
  },
  operationTime: {
    title: '사용 시간을 입력해 주세요',
    description: '',
    invalidFormat: {
      title: '❌ 숫자를 입력 해주세요',
      description: '문자와 특수문자는 입력이 불가능해요!\n소수점 입력이 필요한 경우\n반올림 해주세요!',
    },
  },
} as const;

export const OwnTractorSurveyRequiredInputOverviewPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userOwnTractorSurveySelector = useAppSelector((state) => state.userOwnTractorSurvey);
  const userOwnTractorSurveyActions = userOwnTractorSurveySlice.actions;

  const [selectedBrand, setSelectedBrand] = useState<PickerColumnItem>();
  const [manufacturedYear, setManufacturedYear] = useState<PickerColumnItem>();

  const [model, setModel] = useState<string>();
  const [machineSearchTerm, setMachineSearchTerm] = useState<string>();
  const [engineOutputInPs, setEngineOutputInPs] = useState<number>();
  const [operationTime, setOperationTime] = useState<number>();
  const [isBrandsPickerVisible, setIsBrandsPickerVisible] = useState<boolean>(false);
  const [isModelSearchModalVisible, setIsModelSearchModalVisible] = useState<boolean>(false);
  const [isManufacturedYearPickerVisible, setIsManufacturedYearPickerVisible] = useState<boolean>(false);

  const [isShowDisabledInputModal, setIsShowDisabledInputModal] = useState<boolean>(false);
  const [isShowErrorModal, setIsShowErrorModal] = useState<boolean>(false);

  const [inputError, setInputError] = useState<{ title: string; description: string; errorHandler?: () => void }>();

  const [isEngineOutputInPsFocus, setIsEngineOutputInPsFocus] = useState<boolean>(false);
  const [isOperationTimeFocus, setIsOperationTimeFocus] = useState<boolean>(false);

  const { data: brands, isLoading: isGetBrandsQueryLoading } = useGetBrandsQuery({
    searchParam: {
      applicableMachineType: 'tractor',
    },
  });

  const { data: machines } = useGetMachinesQuery({
    searchParam: {
      brandId: selectedBrand?.value as string,
      fields: 'id,model,engineOutputInPs,approvalStatus',
      searchTerm: machineSearchTerm ?? '',
      machineType: 'tractor',
    },
    options: { enabled: !!selectedBrand, cacheTime: 1000 * 60 * 5 },
  });

  const transformedBrandsForPicker = useMemo<{ label: string; value: string }[][]>(() => {
    if (brands) {
      const transformedBrands = brands.map((brand) => {
        return {
          label: brand.name,
          value: brand.id,
        };
      });

      return [transformedBrands];
    }

    return [];
  }, [brands]);

  const transformedManufacturedYearsForPicker = [
    [...Array(new Date().getFullYear() - 1989)].map((_, i) => ({
      label: `${1990 + i}`,
      value: `${1990 + i}`,
    })),
  ];

  const currentStep = ownTractorSurveySteps.STEP1;
  const steps = Object.values(ownTractorSurveySteps);
  const isRegistration = !userOwnTractorSurveySelector.ownTractorIdForEdit;

  useEffect(() => {
    const { brand, brandId, model, engineOutputInPs, operationTime, manufacturedYear } = userOwnTractorSurveySelector;
    if (brand) {
      setSelectedBrand({ label: brand, value: brandId as string });
    }
    setModel(model);
    setEngineOutputInPs(engineOutputInPs);
    setOperationTime(operationTime);
    setManufacturedYear({ label: manufacturedYear, value: manufacturedYear as number });
  }, [userOwnTractorSurveySelector]);

  const dispatchOwnTractorInputValuesToStore = () =>
    dispatch(
      userOwnTractorSurveyActions.changeOwnTractorSurvey({
        brand: selectedBrand?.label as string,
        brandId: selectedBrand?.value as string,
        model: model as string,
        engineOutputInPs: engineOutputInPs as number,
        manufacturedYear: manufacturedYear?.value as number,
        operationTime: operationTime as number,
      }),
    );

  const getInputError = () => {
    if (!selectedBrand) {
      return ERROR_MESSAGES.brand;
    }

    if (!model) {
      return ERROR_MESSAGES.model;
    }

    if (!engineOutputInPs) {
      setIsEngineOutputInPsFocus(false);
      return {
        ...ERROR_MESSAGES.engineOutputInPs,
        errorHandler: () => setIsEngineOutputInPsFocus(true),
      };
    }

    if (!manufacturedYear?.value) {
      return {
        ...ERROR_MESSAGES.manufacturedYear,
        errorHandler: () => setIsManufacturedYearPickerVisible(true),
      };
    }

    if (!operationTime || !/^[0-9]+$/.test(operationTime.toString())) {
      setIsOperationTimeFocus(false);
      return {
        ...(operationTime ? ERROR_MESSAGES.operationTime.invalidFormat : ERROR_MESSAGES.operationTime),
        errorHandler: () => setIsOperationTimeFocus(true),
      };
    }

    return null;
  };

  useEffect(() => {
    dispatch(userOwnTractorSurveyActions.changeIsOwnTractorSurveyStartedProperly(true));
  }, [dispatch, userOwnTractorSurveyActions]);

  return (
    <>
      <Helmet>
        <title>{ownTractorPageInfo.overviewPage.title}</title>
      </Helmet>

      <OwnTractorSurveyPageWrapper
        headerTitle={'보유하신 트랙터의\n정보를 알려주세요'}
        canConfirm
        steps={steps}
        currentStep={currentStep}
        confirmHandler={() => {
          const validationError = getInputError();
          if (validationError) {
            setInputError(validationError);
            setIsShowErrorModal(true);
            return;
          }
          dispatchOwnTractorInputValuesToStore();
          navigate(ownTractorPageInfo.marketPricePage.path, { replace: true });
        }}
      >
        <SurveyForm padding={'0px 16px'}>
          <SurveyInputHeader inputTitle={'보유 트랙터 등록 필수 정보'} isRequired />
          <SurveyOverviewNavigationList>
            <SurveyNakedInput
              type={'text'}
              label={'브랜드'}
              placeholder={'선택하기'}
              inputValue={selectedBrand?.label as string}
              onClickHandler={() => {
                if (userOwnTractorSurveySelector.ownTractorIdForEdit) {
                  setIsShowDisabledInputModal(true);
                } else {
                  setIsBrandsPickerVisible(true);
                }
              }}
              isEditable={isRegistration}
              isButton
            />
            <SurveyNakedInput
              type={'text'}
              label={'모델'}
              placeholder={'선택하기'}
              inputValue={model ?? ''}
              onClickHandler={() => {
                if (userOwnTractorSurveySelector.ownTractorIdForEdit) {
                  setIsShowDisabledInputModal(true);
                } else {
                  setIsModelSearchModalVisible(true);
                }
              }}
              isEditable={isRegistration}
              isButton
            />
            <SurveyNakedInput
              type={'number'}
              label={'마력'}
              placeholder={'입력하기'}
              inputValue={engineOutputInPs ?? ''}
              onClickHandler={() => {
                if (userOwnTractorSurveySelector.ownTractorIdForEdit) {
                  setIsShowDisabledInputModal(true);
                }
              }}
              onChange={(value: string | number | undefined) => setEngineOutputInPs(value as number | undefined)}
              isEditable={isRegistration}
              isFocus={isEngineOutputInPsFocus}
            />
            <SurveyNakedInput
              type={'number'}
              label={'연식'}
              placeholder={'선택하기'}
              inputValue={manufacturedYear?.value ?? ''}
              onClickHandler={() => setIsManufacturedYearPickerVisible(true)}
              isButton
            />
            <SurveyNakedInput
              type={'number'}
              label={'사용시간'}
              placeholder={'입력하기'}
              inputValue={operationTime ?? ''}
              onChange={(value: string | number | undefined) => setOperationTime(value as number | undefined)}
              isFocus={isOperationTimeFocus}
            />
          </SurveyOverviewNavigationList>
        </SurveyForm>
      </OwnTractorSurveyPageWrapper>

      <SelectPicker
        columns={transformedBrandsForPicker}
        visible={isBrandsPickerVisible}
        onConfirm={(selectedBrandName, extendBrandValue) => {
          setSelectedBrand(extendBrandValue.items[0] as PickerColumnItem);
          setMachineSearchTerm('');
          setModel('');
          setEngineOutputInPs(undefined);
        }}
        onClose={() => setIsBrandsPickerVisible(false)}
      />

      <SelectPicker
        columns={transformedManufacturedYearsForPicker}
        visible={isManufacturedYearPickerVisible}
        defaultValue={'2020'}
        onConfirm={(selectedYear, extendYearValue) => {
          setManufacturedYear(extendYearValue.items[0] as PickerColumnItem);
        }}
        onClose={() => setIsManufacturedYearPickerVisible(false)}
      />

      <SearchModelModal
        isSlideModalOpen={isModelSearchModalVisible}
        closeAll={() => setIsModelSearchModalVisible(false)}
        machines={machines}
        model={model}
        changeMachine={(newMachine: IMachineListMachine) => {
          setModel(newMachine.model);
          setMachineSearchTerm(newMachine.model);
          setEngineOutputInPs(newMachine.engineOutputInPs);
        }}
        machineSearchTerm={machineSearchTerm}
        changeMachineSearchTerm={(newSearchTerm: string) => setMachineSearchTerm(newSearchTerm)}
      />

      <PopupConfirmModal
        isShow={isShowDisabledInputModal}
        hasCloseButton={false}
        closeModal={() => setIsShowDisabledInputModal(false)}
        title={'이 정보를 바꾸시려면\n새로 신청해야해요!'}
        description={'매물의 브랜드, 모델, 마력 정보는\n수정이 불가능합니다.\n메인 화면에서 새로 신청해 주세요!'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => setIsShowDisabledInputModal(false),
            color: 'blue',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isShowErrorModal}
        hasCloseButton={false}
        closeModal={() => setIsShowErrorModal(false)}
        title={inputError?.title ?? ''}
        description={inputError?.description ?? ''}
        buttonArray={[
          {
            text: '확인',
            onClick: () => {
              setIsShowErrorModal(false);
              inputError?.errorHandler?.();
            },
            color: 'blue',
          },
        ]}
      />
    </>
  );
};
